import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Logo from "../assets/logo1.png";

// Import local images
import heroImage from '../assets/hero-industrial.jpg';
import aboutImage from '../assets/about-gas-cylinders.jpg';
import solutionsImage from '../assets/solutions-welding.jpg';

function About() {
  return (
    <div className="relative bg-white min-h-screen font-poppins pt-[160px]">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid-gray/[0.05] bg-[size:60px_60px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#D82F2F]/5 via-transparent to-[#D82F2F]/5"></div>
      </div>

      <div className="relative container mx-auto px-6 py-16">
        {/* Header Section */}
        <div className="text-center mb-20">
          <div className="inline-block">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4 relative">
              About Us
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D82F2F] to-transparent"></div>
            </h1>
          </div>
          <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto leading-relaxed">
            Triveni Industrial Gases - Your trusted partner in industrial gas solutions since establishment. We pride ourselves on quality, reliability, and customer satisfaction.
          </p>
        </div>

        {/* Introduction Section */}
        <div className="relative group mb-20">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
          <div className="relative bg-white border border-[#D82F2F]/20 rounded-xl p-8 shadow-lg">
            <div className="prose prose-lg max-w-none">
              <div className="space-y-6 text-gray-700">
                <p className="text-lg leading-relaxed">
                  <span className="text-[#D82F2F] font-semibold">TIG (Triveni Industrial Gases)</span> an ISO 9001:2015 certified company is a one-stop supplier for welding and industrial solutions, catering to manufacturing, construction, fabrication, and engineering industries. We provide high-quality welding materials, safety equipment, and specialized tools essential for efficient and safe operations.
                </p>
                <p className="text-lg leading-relaxed">
                  Beyond welding supplies, we offer industrial gases such as oxygen, nitrogen, argon, carbon dioxide, and dissolved acetylene, along with gas cylinders, ensuring a steady supply for industrial processes. Our range of power tools, abrasives, and welding spares supports various metalworking applications, makes us a trusted partner for businesses that rely on precision and durability.
                </p>
                <p className="text-lg leading-relaxed">
                  With a focus on quality, reliability, and customer service, our business helps companies maintain productivity and safety in their operations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Company Overview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
            <div className="relative bg-white border border-[#D82F2F]/20 rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-[#D82F2F]/10 rounded-lg">
                  <svg className="w-8 h-8 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 ml-4">Our Company</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p className="text-lg leading-relaxed">
                  Established in Prayagraj, Triveni Industrial Gases has grown to become a leading supplier of industrial gases and related equipment in the region.
                </p>
                <p className="text-lg leading-relaxed">
                  We specialize in providing high-quality industrial gases, welding materials, and safety equipment to various industries while maintaining the highest standards of safety and reliability.
                </p>
              </div>
            </div>
          </div>
          
          <div className="relative group transform hover:scale-105 transition duration-500">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
            <div className="relative overflow-hidden rounded-xl bg-white shadow-lg">
              <img
                src={Logo}
                alt="Triveni Industrial Gases"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        {/* Mission & Values Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* Mission */}
          <div className="relative group transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
            <div className="relative bg-white border border-[#D82F2F]/20 rounded-xl p-8 shadow-lg h-full">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-[#D82F2F]/10 rounded-lg">
                  <svg className="w-8 h-8 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 ml-4">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                To provide superior quality industrial gases and equipment while ensuring safety, reliability, and excellent customer service. We aim to be the most trusted partner for all industrial gas needs in Uttar Pradesh.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="relative group transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
            <div className="relative bg-white border border-[#D82F2F]/20 rounded-xl p-8 shadow-lg h-full">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-[#D82F2F]/10 rounded-lg">
                  <svg className="w-8 h-8 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 ml-4">Our Values</h2>
              </div>
              <ul className="space-y-4 text-lg text-gray-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#D82F2F] rounded-full mr-3"></span>
                  Quality and Safety First
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#D82F2F] rounded-full mr-3"></span>
                  Customer Satisfaction
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#D82F2F] rounded-full mr-3"></span>
                  Reliability & Integrity
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#D82F2F] rounded-full mr-3"></span>
                  Innovation & Excellence
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Certification Section */}
        <div className="relative group mb-20">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#D82F2F] to-[#D82F2F]/80 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
          <div className="relative bg-white border border-[#D82F2F]/20 rounded-xl p-8 shadow-lg text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-[#D82F2F]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#D82F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 ml-4">ISO 9001:2015 Certified</h2>
            </div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We maintain the highest standards of quality management systems, ensuring consistent delivery of products and services that meet customer and regulatory requirements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
